import { useState, useEffect, useCallback, useRef } from "react"
import { useAuth } from "@/lib/auth/context"
import { createClient } from "@/lib/supabase/client"
import { resilientFetch } from "@/lib/network/resilience"
import { useNetworkStatus } from "@/hooks/use-network-status"

// Global cache to persist fetch-once state across component unmounts/remounts
// Keyed by `${url}|${userId || 'anon'}` to scope by user session
const fetchedOnceCache = new Set<string>()

/**
 * Configuration options for the data fetch hook
 */
interface UseDataFetchOptions<T> {
  /**
   * Whether to automatically retry on 401 errors (for cookie sync timing)
   * Default: true
   */
  retryOn401?: boolean
  /**
   * Timeout in milliseconds for the fetch request
   * Default: 15000 (15 seconds)
   */
  timeout?: number
  /**
   * Delay in milliseconds before retrying on 401
   * Default: 500ms
   */
  retryDelay?: number
  /**
   * Whether to wait for auth to finish loading before fetching
   * Default: true
   */
  waitForAuth?: boolean
  /**
   * Delay in milliseconds before initial fetch (helps with cookie sync)
   * Default: 100ms if user exists, 1000ms if no user
   */
  initialDelay?: number
  /**
   * Transform function to process the API response data
   * Useful for data transformation or validation
   */
  transform?: (data: any) => T
  /**
   * Whether to enable debug logging
   * Default: false
   */
  debug?: boolean
  /**
   * Whether fetching is enabled
   * If false, the hook will skip fetching
   * Default: true
   */
  enabled?: boolean
  /**
   * When true, only fetch once after conditions are met.
   * Subsequent dependency changes won't auto-refetch (use refetch() manually).
   * Default: false
   */
  fetchOnce?: boolean
}

/**
 * Base hook for fetching data from API endpoints
 * 
 * Features:
 * - Automatic Authorization header handling (cookies + Bearer token fallback)
 * - 401 retry logic for cookie sync timing
 * - Timeout handling
 * - Loading and error states
 * - Auth-aware fetching (waits for auth to load)
 * 
 * @param url - API endpoint URL to fetch from
 * @param options - Configuration options
 * @returns Object with data, loading, error, and refetch function
 */
export function useDataFetch<T = any>(
  url: string,
  options: UseDataFetchOptions<T> = {}
) {
  const {
    retryOn401 = true,
    timeout = 15000,
    retryDelay = 500,
    waitForAuth = true,
    initialDelay,
    transform,
    debug = false,
    enabled = true,
    fetchOnce = false,
  } = options

  const { user, loading: authLoading } = useAuth()
  const { isOnline } = useNetworkStatus()
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const fetchingRef = useRef(false)
  // Track which cache key was already fetched (instead of just boolean)
  // This allows retrying when user signs in (cache key changes from 'anon' to user ID)
  const didFetchRef = useRef<string | null>(null)
  // Track a scheduled fetch timeout to prevent stacking multiple timers in dev/StrictMode
  const scheduledTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /**
   * Get authentication headers for the request
   * Includes Authorization Bearer token as fallback for immediate post-login
   */
  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = {}
    
    try {
      if (process.env.NODE_ENV === "development") {
        console.log("getAuthHeaders: Creating Supabase client")
      }
      
      const supabaseClient = createClient()
      
      if (process.env.NODE_ENV === "development") {
        console.log("getAuthHeaders: Supabase client created", { hasClient: !!supabaseClient })
      }
      
      if (supabaseClient) {
        if (process.env.NODE_ENV === "development") {
          console.log("getAuthHeaders: Getting session")
        }
        
        // Add timeout to prevent hanging (2 seconds max)
        const sessionPromise = supabaseClient.auth.getSession()
        const timeoutPromise = new Promise<{ data: { session: null }, error: null }>((resolve) => {
          setTimeout(() => {
            if (process.env.NODE_ENV === "development") {
              console.log("getAuthHeaders: Session timeout, continuing without token")
            }
            resolve({ data: { session: null }, error: null })
          }, 2000)
        })
        
        const { data: { session: clientSession }, error: sessionError } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ])
        
        if (process.env.NODE_ENV === "development") {
          console.log("getAuthHeaders: Session retrieved", { 
            hasSession: !!clientSession,
            hasToken: !!clientSession?.access_token,
            error: sessionError 
          })
        }
        
        // If getSession() timed out or failed, try reading from localStorage directly
        let accessToken = clientSession?.access_token
        if (!accessToken && typeof window !== 'undefined') {
          try {
            // Get storage key from Supabase URL (same logic as client.ts)
            const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
            const match = supabaseUrl.match(/https?:\/\/([^.]+)\.supabase\.co/)
            const projectRef = match ? match[1] : 'default'
            const storageKey = `sb-${projectRef}-auth-token`
            
            const storedSession = localStorage.getItem(storageKey)
            if (storedSession) {
              const parsedSession = JSON.parse(storedSession)
              accessToken = parsedSession?.access_token
              
              if (process.env.NODE_ENV === "development") {
                console.log("getAuthHeaders: Found token in localStorage", { hasToken: !!accessToken })
              }
            }
          } catch (localError) {
            if (process.env.NODE_ENV === "development") {
              console.log("getAuthHeaders: localStorage fallback failed", localError)
            }
          }
        }
        
        if (accessToken) {
          headers["Authorization"] = `Bearer ${accessToken}`
        }
      }
    } catch (err) {
      // If getting session fails, continue without Authorization header
      // Cookies will be used as primary auth method
      if (debug || process.env.NODE_ENV === "development") {
        console.error("useDataFetch: Failed to get session for Authorization header", err)
      }
    }
    
    if (process.env.NODE_ENV === "development") {
      console.log("getAuthHeaders: Returning headers", { headerCount: Object.keys(headers).length })
    }
    
    return headers
  }, [debug])

  /**
   * Fetch data from the API endpoint
   */
  const fetchData = useCallback(async (force = false) => {
    // Always log in development to debug fetch issues
    if (process.env.NODE_ENV === "development") {
      console.log("useDataFetch: fetchData called", url, { force, fetching: fetchingRef.current })
    }
    
    // Prevent concurrent fetches
    if (fetchingRef.current) {
      if (debug || process.env.NODE_ENV === "development") {
        console.log("useDataFetch: Already fetching, skipping...", url)
      }
      return
    }

    // If force is true (explicit refetch), clear fetchOnce flags to allow refetch
    if (force && fetchOnce) {
      didFetchRef.current = null
      const cacheKey = `${url}|${user?.id || 'anon'}`
      fetchedOnceCache.delete(cacheKey)
      try {
        if (typeof window !== 'undefined') {
          const storageKey = `useDataFetch:fetchedOnce:${cacheKey}`
          window.sessionStorage.removeItem(storageKey)
        }
      } catch {}
    }

    fetchingRef.current = true

    try {
      setLoading(true)
      setError(null)

      if (process.env.NODE_ENV === "development") {
        console.log("useDataFetch: Starting fetch process", url, { isOnline })
      }

      // Check if offline before attempting fetch
      if (!isOnline) {
        if (process.env.NODE_ENV === "development") {
          console.error("useDataFetch: Offline, aborting fetch", url)
        }
        throw new Error('network/offline')
      }

      // Get authentication headers (skip for public endpoints or if retryOn401 is false)
      let headers: Record<string, string> = {}
      if (retryOn401 || user) {
        if (process.env.NODE_ENV === "development") {
          console.log("useDataFetch: Getting auth headers", url)
        }
        
        try {
          headers = await getAuthHeaders()
          
          if (process.env.NODE_ENV === "development") {
            console.log("useDataFetch: Got auth headers", url, { headerCount: Object.keys(headers).length })
          }
        } catch (err) {
          // If getting headers fails, continue without them (cookies will be used)
          if (process.env.NODE_ENV === "development") {
            console.warn("useDataFetch: Failed to get auth headers, continuing without them", url, err)
          }
        }
      } else {
        if (process.env.NODE_ENV === "development") {
          console.log("useDataFetch: Skipping auth headers (public endpoint)", url)
        }
      }

      if (debug || process.env.NODE_ENV === "development") {
        console.log("useDataFetch: Fetching", url, { 
          hasUser: !!user,
          isOnline,
          headers: Object.keys(headers),
        })
      }

      // Use resilient fetch with exponential backoff and circuit breaker
      // Note: 401 retries are handled separately below for auth timing
      let response: Response
      try {
        if (debug || process.env.NODE_ENV === "development") {
          console.log("useDataFetch: About to call resilientFetch", url, {
            credentials: "include",
            hasHeaders: Object.keys(headers).length > 0,
          })
        }
        
        response = await resilientFetch(url, {
          credentials: "include",
          headers,
          retryOptions: {
            maxRetries: 2, // Limited retries for network resilience (401 has separate logic)
            initialDelay: 500,
            maxDelay: 5000,
            retryableStatuses: [500, 502, 503, 504, 408, 429], // Server errors and timeouts
          },
        })

        if (debug || process.env.NODE_ENV === "development") {
          console.log("useDataFetch: Response received", url, {
            status: response?.status,
            ok: response?.ok,
            statusText: response?.statusText,
          })
        }
      } catch (fetchError: any) {
        if (debug || process.env.NODE_ENV === "development") {
          console.error("useDataFetch: resilientFetch error", url, {
            error: fetchError,
            message: fetchError?.message,
            name: fetchError?.name,
            stack: fetchError?.stack,
          })
        }
        throw fetchError
      }

      // Handle 401 retry if enabled
      if (!response.ok && response.status === 401 && retryOn401 && user) {
        if (debug) {
          console.log(
            "useDataFetch: Got 401, retrying after delay",
            url,
            retryDelay + "ms"
          )
        }
        await new Promise((resolve) => setTimeout(resolve, retryDelay))

        // Use resilient fetch for 401 retry as well
        response = await resilientFetch(url, {
          credentials: "include",
          headers,
          retryOptions: {
            maxRetries: 0, // Don't retry again - this is already a retry
            initialDelay: 0,
          },
        })

        if (debug) {
          console.log("useDataFetch: Retry response", url, {
            status: response?.status,
            ok: response?.ok,
          })
        }
      }

      // Handle non-OK responses
      if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error")
        if (debug) {
          console.error("useDataFetch: API error", url, {
            status: response.status,
            error: errorText,
          })
        }
        throw new Error(`Failed to fetch: ${response.status}`)
      }

      // Parse response
      const result = await response.json()

      if (debug) {
        console.log("useDataFetch: Response data", url, {
          success: result?.success,
          hasData: !!result?.data,
          dataKeys: result?.data ? Object.keys(result.data) : [],
          plannedSessionsCount: result?.data?.plannedSessions?.length,
        })
      }

      // Check for success flag in response
      if (result.success === false) {
        throw new Error(result.error || "API returned success: false")
      }

      // Extract data (handle both { success: true, data: ... } and direct data)
      const responseData = result.data !== undefined ? result.data : result

      // Apply transform if provided
      const transformedData = transform ? transform(responseData) : responseData

      setData(transformedData)
      // Mark as fetched on success - track the cache key that was fetched
      const cacheKey = `${url}|${user?.id || 'anon'}`
      didFetchRef.current = cacheKey
      // Also persist in module-level cache to survive remounts in dev/StrictMode
      fetchedOnceCache.add(cacheKey)
      // Persist to sessionStorage as well to survive HMR/Fast Refresh in dev
      try {
        if (typeof window !== 'undefined' && fetchOnce) {
          const storageKey = `useDataFetch:fetchedOnce:${cacheKey}`
          window.sessionStorage.setItem(storageKey, '1')
        }
      } catch {}
    } catch (err: any) {
      // Always log errors in development, even if debug is off
      if (debug || process.env.NODE_ENV === "development") {
        console.error("useDataFetch: Error", url, {
          error: err,
          message: err?.message,
          stack: err?.stack,
          name: err?.name,
        })
      }

      // Don't show error for timeouts - just log it
      if (!err?.message?.includes("timeout")) {
        setError(err.message || "Failed to load data")
      }

      // Set data to null on error (or keep previous data if preferred)
      setData(null)
    } finally {
      setLoading(false)
      fetchingRef.current = false

      if (debug) {
        console.log("useDataFetch: Finished", url)
      }
    }
  }, [url, user, retryOn401, retryDelay, timeout, getAuthHeaders, transform, debug, isOnline])

  /**
   * Effect to trigger fetch when conditions are met
   */
  useEffect(() => {
    // Determine cache key for fetch-once behavior across remounts
    // Calculate this first so we can use it for clearing old cache entries
    const cacheKey = `${url}|${user?.id || 'anon'}`
    // Also derive a storage key to persist across HMR/Fast Refresh in dev
    const storageKey = `useDataFetch:fetchedOnce:${cacheKey}`
    
    // When user changes (signs in/out), clear the old cache entry to allow refetch
    // This ensures that when a user signs in, we fetch even if we previously fetched as 'anon'
    // Also handle case where didFetchRef is still boolean (old code) - treat as cache miss
    const currentDidFetch = didFetchRef.current
    const isOldBooleanValue = currentDidFetch === true || currentDidFetch === false
    
    if (user && currentDidFetch && !isOldBooleanValue && currentDidFetch !== cacheKey) {
      // User changed - clear old cache entry
      const oldCacheKey = currentDidFetch
      fetchedOnceCache.delete(oldCacheKey)
      try {
        if (typeof window !== 'undefined') {
          const oldStorageKey = `useDataFetch:fetchedOnce:${oldCacheKey}`
          window.sessionStorage.removeItem(oldStorageKey)
        }
      } catch {}
      // Reset ref to allow new fetch
      didFetchRef.current = null
    } else if (isOldBooleanValue && user) {
      // Old code detected - reset to allow fetch with new user
      didFetchRef.current = null
      if (process.env.NODE_ENV === "development") {
        console.log("useDataFetch: Detected old boolean ref, resetting for user change", url, { userId: user.id })
      }
    }
    
    // Skip fetching if disabled
    // Compute enabled state from user if not explicitly provided (for backward compatibility)
    const isEnabled = enabled !== undefined ? enabled : !!user
    
    if (!isEnabled) {
      setLoading(false)
      if (debug || process.env.NODE_ENV === "development") {
        console.log("useDataFetch: Fetching disabled, skipping...", url, { user: !!user, enabled, isEnabled, userId: user?.id })
      }
      // Clear any scheduled timeout if disabled while pending
      if (scheduledTimeoutRef.current) {
        clearTimeout(scheduledTimeoutRef.current)
        scheduledTimeoutRef.current = null
      }
      return
    }

    // Skip if configured to fetch once and we already fetched
    // Checks cover: same-mount ref, module-level Set (StrictMode remounts), and sessionStorage (HMR)
    let fetchedOncePersisted = false
    try {
      if (typeof window !== 'undefined') {
        fetchedOncePersisted = window.sessionStorage.getItem(storageKey) === '1'
      }
    } catch {}

    // If user just signed in (didFetchRef is null) but sessionStorage says we fetched,
    // clear it to allow fresh fetch after sign-in (data might be stale or user context changed)
    if (user && !currentDidFetch && fetchedOncePersisted) {
      if (process.env.NODE_ENV === "development") {
        console.log("useDataFetch: User signed in, clearing stale sessionStorage cache", url, { cacheKey })
      }
      try {
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(storageKey)
        }
      } catch {}
      fetchedOncePersisted = false
      // Also clear from module cache
      fetchedOnceCache.delete(cacheKey)
    }

    // Skip if configured to fetch once and we already fetched for this specific cache key
    // Use refs and cache to avoid dependency on data state (which would cause infinite loops)
    // Check if we already fetched for this exact cache key (user ID changes after login)
    // Handle old boolean value (from previous code version) - treat as cache miss if user changed
    // Note: currentDidFetch and isOldBooleanValue are already declared above
    const cacheKeyMatches = !isOldBooleanValue && currentDidFetch === cacheKey
    
    const alreadyFetched = fetchOnce && (
      cacheKeyMatches || 
      fetchedOnceCache.has(cacheKey) || 
      fetchedOncePersisted
    )
    
    if (alreadyFetched) {
      if (debug) {
        console.log("useDataFetch: fetchOnce enabled and already fetched, skipping...", url, { cacheKey, didFetchKey: didFetchRef.current })
      }
      setLoading(false)
      // Ensure no stray timers remain scheduled
      if (scheduledTimeoutRef.current) {
        clearTimeout(scheduledTimeoutRef.current)
        scheduledTimeoutRef.current = null
      }
      return
    }

    // Wait for auth to finish loading if enabled
    if (waitForAuth && authLoading) {
      if (debug) {
        console.log("useDataFetch: Auth still loading, waiting...", url)
      }
      return
    }

    // Calculate initial delay
    // After sign-in, cookies need time to be set by middleware (runs on page navigation)
    // Give more time after fresh login to ensure cookies are available
    const delay =
      initialDelay !== undefined
        ? initialDelay
        : user
          ? 1000 // User exists - give middleware time to set cookies after login
          : 1000 // No user yet, give more time for cookie sync

    // Set up delayed fetch
    // Avoid stacking multiple scheduled calls
    if (scheduledTimeoutRef.current) {
      clearTimeout(scheduledTimeoutRef.current)
      scheduledTimeoutRef.current = null
    }
    
    if (process.env.NODE_ENV === "development") {
      console.log("useDataFetch: Scheduling fetch", url, { delay, user: !!user })
    }
    
    scheduledTimeoutRef.current = setTimeout(() => {
      scheduledTimeoutRef.current = null
      if (process.env.NODE_ENV === "development") {
        console.log("useDataFetch: Executing scheduled fetch", url)
      }
      fetchData()
    }, delay)

    return () => {
      if (scheduledTimeoutRef.current) {
        clearTimeout(scheduledTimeoutRef.current)
        scheduledTimeoutRef.current = null
      }
    }
  }, [user, authLoading, waitForAuth, fetchData, initialDelay, debug, url, enabled, fetchOnce, isOnline])

  // Refetch function that forces a new fetch even if fetchOnce is enabled
  const refetch = useCallback(async () => {
    return fetchData(true)
  }, [fetchData])

  return {
    data,
    loading,
    error,
    refetch,
  }
}




